import {
  FormLabelInput,
  TitleInputContainer,
  TitleDescription,
  MessageInputContainer,
  FormLabel,
  InputContainer,
  FormInput,
  FormNote,
} from "@/components/ui/form-elements";
import {
  Select,
  SelectContent,
  SelectItem,
  ClearableSelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import EdsSearchableSelect from "../EdsSearchableSelect";
import NodeContext from "../../context/NodeContext";
import { useContext, useState } from "react";
import {
  dateFormatOptions,
  inputSubTypeOptions,
  toolbar,
} from "../../utils/constants";
import { Editor } from "react-draft-wysiwyg";
import { getHTMLToDraft } from "../../utils/helper";
import ResponseVariable from "../ResponseVariable/index";
import { Tooltip } from "@mui/material";
import { AlertTriangle } from "lucide-react";
import { LABELS } from "../../utils/labels";
import { Switch } from "@/components/ui/switch";
import { FLOW_TYPE_SCREENING, FLOW_TYPE_RECONNECT, FLOW_TYPE_FAQ } from "../../constants";

function CollectInputNodeEditor() {
  const {
    selectedNodeInfo,
    updateNode,
    variableList,
    atsData,
    isEditable,
    saveData,
    setNewAddedNode,
    atsPlatform,
    profileType,
  } = useContext(NodeContext);
  const [displayError, setDisplayError] = useState(
    selectedNodeInfo.data.isVariableNameAlreadyUsed ?? false
  );

  if (!selectedNodeInfo) {
    return null;
  }

  return (
    <>
      <TitleInputContainer>
        <FormLabelInput
          placeholder="Label"
          value={selectedNodeInfo?.data?.titleText}
          onChange={(e) => {
            updateNode(selectedNodeInfo.id, {
              ...selectedNodeInfo?.data,
              titleText: e.target.value,
            });
          }}
          readOnly={!isEditable}
        />
        <TitleDescription>Collect input asks a question to a user which requires an answer</TitleDescription>
      </TitleInputContainer>
      <MessageInputContainer>
        <div className="flex w-full items-center justify-between">
          <FormLabel className="mb-0">{LABELS.COLLECT_INPUT_NODE_MESSAGE_BODY}</FormLabel>
          {[FLOW_TYPE_SCREENING, FLOW_TYPE_RECONNECT, FLOW_TYPE_FAQ].includes(profileType) && (
            <Tooltip title="When enabled, the candidate can skip this question without providing an answer">
              <div className="flex items-center gap-2">
                <FormLabel className="mb-0 font-12px">{LABELS.SKIPPABLE}</FormLabel>
                <Switch
                  checked={selectedNodeInfo?.data?.isSkippable ?? false}
                  onCheckedChange={(checked) => {
                    updateNode(selectedNodeInfo.id, {
                      ...selectedNodeInfo?.data,
                      isSkippable: checked,
                      ...(!checked && { skipToNodeId: "" }),
                    });
                  }}
                  disabled={!isEditable}
                />
              </div>
            </Tooltip>
          )}
        </div>
        <Editor
          editorState={getHTMLToDraft(selectedNodeInfo?.data?.message)}
          onEditorStateChange={(data) => {
            const isMessageEmpty = data.getCurrentContent().hasText();
            updateNode(selectedNodeInfo.id, {
              ...selectedNodeInfo?.data,
              message: data,
              isMessageEmpty: !isMessageEmpty,
            });
          }}
          onBlur={() => {
            setNewAddedNode(true);
          }}
          wrapperClassName="wrapper-class"
          editorClassName="editor-class"
          toolbarClassName="toolbar-class"
          toolbar={toolbar}
          mention={{
            separator: " ",
            trigger: "{",
            suggestions: variableList,
          }}
          placeholder="What is your..."
          readOnly={!isEditable}
          stripPastedStyles={true}
        />
      </MessageInputContainer>
      <InputContainer>
        <FormLabel>{LABELS.RESPONSE_TYPE}</FormLabel>
        <Select
          value={selectedNodeInfo?.data?.subType || "text"}
          onValueChange={(value) => {
            const updatedData = {
              ...selectedNodeInfo?.data,
              subType: value,
            };
            if (value === "address") {
              updatedData.atsValue = null;
            }
            updateNode(selectedNodeInfo.id, updatedData);
          }}
          disabled={!isEditable}
        >
          <ClearableSelectTrigger 
            className="w-full"
            showClear={!!selectedNodeInfo?.data?.subType && selectedNodeInfo?.data?.subType !== "text"}
            onClear={() => {
              updateNode(selectedNodeInfo.id, {
                ...selectedNodeInfo?.data,
                subType: "text",
              });
            }}
          >
            <SelectValue placeholder="Select subtype" />
          </ClearableSelectTrigger>
          <SelectContent>
            {inputSubTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {(atsPlatform === "VINCERE" || atsPlatform === "JOBADDER" || atsPlatform === "FASTTRACK") && (
              <SelectItem key="address" value="address">
                Address
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </InputContainer>
      {selectedNodeInfo?.data?.subType === "regex" ? (
        <InputContainer>
          <FormLabel>{LABELS.REGEX}</FormLabel>
          <FormInput
            placeholder="Enter Regular Expression"
            value={selectedNodeInfo?.data?.regexValue}
            onChange={(e) => {
              updateNode(selectedNodeInfo.id, {
                ...selectedNodeInfo?.data,
                regexValue: e.target.value,
              });
            }}
            readOnly={!isEditable}
          />
        </InputContainer>
      ) : selectedNodeInfo?.data?.subType === "date" ? (
        <MessageInputContainer>
          <FormLabel>{LABELS.DATE_FORMAT}</FormLabel>
          <Select
            value={selectedNodeInfo?.data?.dateFormat || ""}
            onValueChange={(value) => {
              updateNode(selectedNodeInfo.id, {
                ...selectedNodeInfo?.data,
                dateFormat: value,
              });
            }}
            disabled={!isEditable}
          >
            <ClearableSelectTrigger 
              className="w-full"
              showClear={!!selectedNodeInfo?.data?.dateFormat}
              onClear={() => {
                updateNode(selectedNodeInfo.id, {
                  ...selectedNodeInfo?.data,
                  dateFormat: "",
                });
              }}
            >
              <SelectValue placeholder="Select date format" />
            </ClearableSelectTrigger>
            <SelectContent>
              {dateFormatOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </MessageInputContainer>
      ) : null}
      <InputContainer>
        <FormLabel>{LABELS.INPUT_SUGGESTION}</FormLabel>
        <FormInput
          placeholder="Enter Suggestion"
          value={selectedNodeInfo?.data?.suggestionText}
          onChange={(e) => {
            updateNode(selectedNodeInfo.id, {
              ...selectedNodeInfo?.data,
              suggestionText: e.target.value,
            });
          }}
          readOnly={!isEditable}
        />
      </InputContainer>
      <InputContainer>
        <FormLabel>{LABELS.INPUT_VALIDATION}</FormLabel>
        <FormInput
          placeholder="Enter Validation Message"
          value={selectedNodeInfo?.data?.validationText}
          onChange={(e) => {
            updateNode(selectedNodeInfo.id, {
              ...selectedNodeInfo?.data,
              validationText: e.target.value,
            });
          }}
          readOnly={!isEditable}
        />
      </InputContainer>
      <InputContainer>
        {atsData && (
          <>
            <FormLabel>{LABELS.ATS_FIELD}</FormLabel>
            <EdsSearchableSelect
              options={atsData}
              value={selectedNodeInfo?.data?.atsValue || ""}
              onChange={(value) => {
                updateNode(selectedNodeInfo.id, {
                  ...selectedNodeInfo?.data,
                  atsValue: value || null,
                });
                setTimeout(() => {
                  saveData();
                }, 10);
              }}
              isDisabled={!isEditable || selectedNodeInfo?.data?.subType === "address"}
              placeholder="Select ATS Field"
            />
          </>
        )}
        <div className="mt-2.5">
          <ResponseVariable
            updateNodeFun={updateNode}
            selectedNodeInfo={selectedNodeInfo}
            iseditable={isEditable ? "true" : undefined}
          />
        </div>
        <div className="relative flex w-full items-center justify-between gap-0.5">
          <FormInput
            placeholder="Enter Variable Name"
            value={selectedNodeInfo?.data?.variableName}
            onBlur={(e) => {
              setNewAddedNode(true);
            }}
            onChange={(e) => {
              if (e.target.value.length > 60) {
                return;
              }
              const isVariableNameAlreadyUsed = variableList.find(
                (item) => item.value === `{${e.target.value.trim()}}}`
              );
              if (isVariableNameAlreadyUsed) {
                setDisplayError(true);
              } else {
                setDisplayError(false);
              }
              updateNode(selectedNodeInfo.id, {
                ...selectedNodeInfo?.data,
                variableName: e.target.value,
                isVariableNameAlreadyUsed: isVariableNameAlreadyUsed
                  ? true
                  : false,
              });
            }}
            readOnly={!isEditable}
          />
          {displayError && (
            <Tooltip title="This variable has already been used.">
              <AlertTriangle
                className="absolute right-10 text-[#ffcc00]"
                size={18}
              />
            </Tooltip>
          )}
        </div>
        <FormNote>
          You can add a variable that can be referenced later in the
          conversation.
        </FormNote>
      </InputContainer>
    </>
  );
}

export default CollectInputNodeEditor;
